/**
 * Sidebar Component - Two-Level Navigation
 *
 * Sistema de navegación de dos niveles:
 * - Sidebar izquierdo: Iconos principales (sin fondo)
 * - Sidebar derecho: Submenús del item seleccionado (con glassmorphism)
 *
 * @created 2025-01-08
 */

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { apiClient } from '@/lib/api-client';
import { gsap } from 'gsap';
import { OwlyCrmSidebarProps } from './types/sidebar.types';
import { useSidebarNavigation } from './hooks/useSidebarNavigation';
import { useSidebarPermissions } from './hooks/useSidebarPermissions';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { useTesting } from '@/shared/contexts/TestingContext';
import { useCompanies } from '@/hooks/useCompanies';
import { useProjects } from '@/hooks/useProjects';
import { useMLInsightsBadge } from '@/hooks/useMLInsightsNotifications';
import { useConversations } from '@/shared/hooks/useCommunications';
import { useProjectOrbitViews } from '@/hooks/useOrbitViews';
import { useAuth } from '@/shared/contexts/AuthContext';
import { useTheme } from '@/shared/contexts/ThemeContext';
import { createTenantSections } from './navigation/tenant-sections';
// REMOVED: owly-sections.ts - Now all navigation comes from MODULES_CONFIG via tenant-sections
import { createAdvisorSections } from './navigation/advisor-sections';
// REMOVED: ProjectSelector - Now using TenantSelector on pages instead
import { OwlyLogo } from '@/components/ui/owly-logo';
import { Switch } from '@/catalyst-ui-kit/typescript/switch';
import { ArrowRightOnRectangleIcon, CubeTransparentIcon, PencilIcon } from '@heroicons/react/24/outline';
import { SidebarPromoCard } from './components/SidebarPromoCard';
import {
  SunIcon,
  MoonIcon,
  BellIcon,
  QuestionMarkCircleIcon,
  CogIcon,
} from './navigation/icons';
import { Tooltip } from '@/shared/components/ui';
import { isItemActive } from './utils/sidebar.utils';
import { useTenant } from '@/shared/contexts/TenantContext';
import { NavigationItem } from './types/sidebar.types';
import { ROLE_CONFIG } from '@/modules/users/constants/roles';

/** Company icon for sidebar — shows sidebar_icon (light/dark), logo, or name initial as fallback */
function CompanyIcon({ companyData, theme }: { companyData: { name: string; logo?: string | null; sidebar_icon_light?: string | null; sidebar_icon_dark?: string | null }; theme: string }) {
  const iconUrl = theme === 'dark'
    ? (companyData.sidebar_icon_dark || companyData.sidebar_icon_light || companyData.logo)
    : (companyData.sidebar_icon_light || companyData.logo);

  if (iconUrl) {
    return (
      <div className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center">
        <img src={iconUrl} alt={companyData.name} className="h-full w-full object-contain" />
      </div>
    );
  }

  return (
    <div className="w-12 h-12 rounded-xl overflow-hidden bg-white dark:bg-gray-800 ring-1 ring-gray-200 dark:ring-gray-700 flex items-center justify-center">
      <span className="text-2xl font-bold text-gray-900 dark:text-white">
        {companyData.name[0]}
      </span>
    </div>
  );
}

/** User avatar — shows photo or initials fallback */
function UserAvatar({ user, size = 48 }: { user: any; size?: number }) {
  const initials = `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase() || '?';
  return (
    <div
      className="rounded-full overflow-hidden flex items-center justify-center bg-primary-500 text-white flex-shrink-0"
      style={{ width: size, height: size }}
    >
      {user.avatar ? (
        <img src={user.avatar} alt={user.full_name} className="h-full w-full object-cover" />
      ) : (
        <span className={size >= 48 ? 'text-lg font-semibold' : 'text-xs font-semibold'}>{initials}</span>
      )}
    </div>
  );
}

/** Profile popover — appears when clicking the user avatar in the sidebar */
function UserProfilePopover({
  user,
  isOpen,
  onClose,
  onLogout,
  onEditProfile,
  anchorRef,
}: {
  user: any;
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
  onEditProfile: () => void;
  anchorRef: React.RefObject<HTMLDivElement | null>;
}) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const roleName = user.role && ROLE_CONFIG[user.role as keyof typeof ROLE_CONFIG]?.name || user.role || 'Usuario';

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        anchorRef.current && !anchorRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen, onClose, anchorRef]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Position: to the right of the anchor
  const anchorRect = anchorRef.current?.getBoundingClientRect();
  const top = anchorRect ? anchorRect.top - 150 : 100;
  const left = anchorRect ? anchorRect.right + 12 : 80;

  return createPortal(
    <div
      ref={popoverRef}
      className="fixed z-[99999] w-72 bg-white dark:bg-gray-900 rounded-2xl shadow-2xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden"
      style={{ top: Math.max(12, top), left }}
    >
      {/* User info header */}
      <div className="p-4 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center gap-3">
          <div className="relative">
            <UserAvatar user={user} size={48} />
            <div className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-500 ring-2 ring-white dark:ring-gray-900" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-bold text-gray-900 dark:text-white truncate">
              {user.full_name || user.email}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
            <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              {roleName}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="p-2">
        <button
          onClick={() => { onEditProfile(); onClose(); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <PencilIcon className="h-4 w-4" />
          Editar Perfil
        </button>
      </div>

      {/* Logout */}
      <div className="border-t border-gray-200 dark:border-gray-700 p-2">
        <button
          onClick={() => { onLogout(); onClose(); }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
        >
          <ArrowRightOnRectangleIcon className="h-4 w-4" />
          Cerrar Sesión
        </button>
      </div>
    </div>,
    document.body
  );
}

/** Edit Profile Modal */
function EditProfileModal({
  user,
  isOpen,
  onClose,
  onSaved,
}: {
  user: any;
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [firstName, setFirstName] = useState(user.first_name || '');
  const [lastName, setLastName] = useState(user.last_name || '');
  const [phone, setPhone] = useState(user.phone || '');
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const roleName = user.role && ROLE_CONFIG[user.role as keyof typeof ROLE_CONFIG]?.name || user.role || 'Usuario';

  // Sync state when user changes or modal opens
  useEffect(() => {
    if (isOpen) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setPhone(user.phone || '');
      setAvatarPreview(null);
      setAvatarFile(null);
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const handleAvatarSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarFile(file);
    setAvatarPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Upload avatar first if changed
      if (avatarFile) {
        const formData = new FormData();
        formData.append('avatar', avatarFile);
        await (apiClient as any).request('/auth/users/profile/', {
          method: 'PATCH',
          body: formData,
        });
      }

      // Update text fields
      await apiClient.patch('/auth/users/profile/', {
        first_name: firstName,
        last_name: lastName,
        phone,
      });

      // Refresh auth context with new data
      await onSaved();
      onClose();
    } catch (err) {
      console.error('Error updating profile:', err);
    } finally {
      setSaving(false);
    }
  };

  // Avatar display: preview > current avatar > initials
  const displayAvatarUrl = avatarPreview || user.avatar;
  const initials = `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase() || '?';

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="p-6 pb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Editar Perfil</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Actualiza tu información personal</p>
        </div>

        {/* Avatar + Role */}
        <div className="px-6 pb-4 flex items-center gap-4">
          <div
            className="relative group cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
          >
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center bg-primary-500 text-white flex-shrink-0">
              {displayAvatarUrl ? (
                <img src={displayAvatarUrl} alt={user.full_name} className="h-full w-full object-cover" />
              ) : (
                <span className="text-lg font-semibold">{initials}</span>
              )}
            </div>
            <div className="absolute inset-0 rounded-full bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <PencilIcon className="h-5 w-5 text-white" />
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarSelect}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.email}</p>
            <span className="inline-flex items-center mt-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
              {roleName}
            </span>
            {avatarPreview && (
              <p className="text-[10px] text-primary-500 mt-1">Foto seleccionada — guardar para aplicar</p>
            )}
          </div>
        </div>

        {/* Form */}
        <div className="px-6 pb-4 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Nombre</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Apellido</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Teléfono</label>
            <input
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export const Sidebar: React.FC<OwlyCrmSidebarProps> = ({
  currentContext: initialContext,
  onContextChange,
  currentPage = '/dashboard',
  leadCount = 0,
  clientCount = 0,
  className = '',
}) => {
  // ===========================
  // HOOKS & STATE
  // ===========================

  const navigation = useSidebarNavigation(initialContext, onContextChange);
  const { owlyMode } = useTesting();
  const { hasSubmodulePermission } = usePermissions();
  const { user: authUser, logout, isLoading: authLoading, refreshUser } = useAuth();
  const { theme, setTheme, toggleTheme } = useTheme();
  // Use TenantContext as unified source of truth for project data
  const { activePipelineView, selectedProject, projects: accessibleProjects, isPlatform } = useTenant();

  // ===========================
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS
  // (React Hooks rules: hooks must be called in the same order every render)
  // ===========================

  // Fetch companies count for badge
  const { data: companiesData } = useCompanies();
  const companiesCount = companiesData?.results?.length || 0;

  // Fetch projects count for badge
  // Platform users: fetch all projects via useProjects
  // Tenant users: use accessibleProjects from TenantContext (respects ProjectMembership)
  const { data: platformProjectsData } = useProjects();
  const projectsCount = isPlatform
    ? (platformProjectsData?.results?.length || platformProjectsData?.length || 0)
    : accessibleProjects.length;

  // Fetch ML Insights urgent count for badge
  const mlInsightsBadge = useMLInsightsBadge();

  // Fetch conversations for sidebar badges (unread team/direct count)
  // NOTE: We don't use has_unread backend filter because team conversations
  // use per-user ConversationReadState, not the global has_unread_messages flag.
  // Instead, fetch convos and count team/direct ones with unread_count > 0.
  const { data: allConvosForBadge } = useConversations({ limit: 50 });
  const unreadTeamCount = allConvosForBadge?.results?.filter(
    (c: any) => (c.conversation_type === 'team' || c.conversation_type === 'direct') && (c.unread_count || 0) > 0
  ).length || 0;

  // OrbitView integration - using centralized hook instead of manual API calls
  const { data: projectOrbitViews = [], isLoading: loadingOrbitViews } = useProjectOrbitViews(
    selectedProject?.id,
    selectedProject?.slug
  );

  // State para modal de confirmación de logout, profile popover y edit profile modal
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showProfilePopover, setShowProfilePopover] = useState(false);
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const avatarRef = useRef<HTMLDivElement>(null);

  // State para controlar qué sección está seleccionada en el sidebar izquierdo
  // Initialize to null - will be calculated on first render based on current path
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [shouldAnimate, setShouldAnimate] = useState<boolean>(false);
  // Track if menu items are visible (false = opacity 0 for animation, true = opacity 1)
  const [menuItemsVisible, setMenuItemsVisible] = useState<boolean>(true);

  // Track the last path we synced to avoid unnecessary re-syncs
  const lastSyncedPathRef = useRef<string | null>(null);

  // Refs para animaciones GSAP
  const sectionTitleRef = useRef<HTMLDivElement>(null);
  const menuItemsRef = useRef<HTMLDivElement>(null);

  // ===========================
  // EARLY RETURN - LOADING STATE
  // (Now safe because all hooks are called above)
  // ===========================
  if (authLoading) {
    return (
      <div className={`flex h-full ${className}`}>
        <div className="w-20 h-full p-3 flex flex-col items-center">
          <div className="h-[72px] flex items-center justify-center">
            <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700 animate-pulse" />
          </div>
          <div className="w-12 h-px bg-gray-300 dark:bg-gray-600 mb-4" />
          <div className="flex-1 flex flex-col gap-2 items-center">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse" />
            ))}
          </div>
        </div>
        <div className="w-64 h-full p-3 pl-0">
          <div className="flex h-full flex-col bg-white/40 dark:bg-gray-800/40 backdrop-blur-xl rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  /**
   * SIMPLIFIED: Check if item is active for pipeline funnel items
   * Logic: If URL starts with /pipelines/, activate the correct funnel button
   */
  const isItemActiveEnhanced = (currentPath: string, item: NavigationItem): boolean => {
    // SIMPLIFIED: For pipeline funnels, just check if we're on /pipelines/*
    if (item.id === 'funnel-leads' || item.id === 'funnel-clientes') {
      // Check if we're on any pipeline route
      if (currentPath.startsWith('/pipelines/') || currentPath === '/pipelines') {
        // Determine which type is active
        // Priority 1: Use activePipelineView from context (most reliable)
        // Priority 2: Use URL query param ?type=
        // Priority 3: Default to 'lead'
        let activeType: 'lead' | 'client' = 'lead';

        if (activePipelineView) {
          activeType = activePipelineView;
        } else {
          // Fallback: check URL query param
          const typeParam = new URLSearchParams(window.location.search).get('type');
          if (typeParam === 'client') {
            activeType = 'client';
          }
        }

        // Activate the matching button
        if (item.id === 'funnel-leads') {
          return activeType === 'lead';
        }
        if (item.id === 'funnel-clientes') {
          return activeType === 'client';
        }
      }
      return false; // Not on pipeline route
    }

    // For all other items, use standard path matching
    return isItemActive(currentPath, item.href, item.matchPaths);
  };

  // NOTE: OrbitViews are now loaded via useProjectOrbitViews hook (line ~85)
  // This eliminates the manual API call and leverages React Query's caching

  // Choose navigation sections based on user type
  // UNIFIED ARCHITECTURE: All navigation comes from MODULES_CONFIG via createTenantSections
  // Filtering by scope (platform/tenant/shared) is handled in useSidebarPermissions
  const allSections = React.useMemo(() => {
    // ALL users use the same MODULES_CONFIG
    // - Platform users (OWLY team): Will see platform-scoped + shared modules
    // - Tenant users (constructoras): Will see tenant-scoped + shared modules
    // - Superusers: Will see ALL modules
    // The filtering is done in useSidebarPermissions based on scope
    return createTenantSections(leadCount, clientCount);
  }, [leadCount, clientCount]);

  // Add dynamic badges
  const sectionsWithDynamicBadges = React.useMemo(() => {
    return allSections.map(section => ({
      ...section,
      items: section.items?.map(item => {
        if (item.id === 'companies-list') {
          return { ...item, badge: undefined };
        }
        if (item.id === 'proyectos') {
          return { ...item, badge: undefined };
        }
        if (item.id === 'ml-insights') {
          return { ...item, badge: undefined };
        }
        return item;
      }),
    }));
  }, [allSections, companiesCount, projectsCount, mlInsightsBadge]);

  const permissions = useSidebarPermissions(sectionsWithDynamicBadges, navigation.currentContext);

  // Ref para evitar sincronización cuando el cambio viene del sidebar
  const isNavigatingFromSidebar = useRef(false);

  // Helper para normalizar hrefs (asegurar que siempre tengan / al inicio, sin //)
  const normalizeHref = (href: string): string => {
    const cleanHref = href.startsWith('/') ? href : `/${href}`;
    return cleanHref;
  };

  /**
   * STRUCTURAL FIX: Robust sidebar sync with current path
   *
   * This effect handles three scenarios:
   * 1. Initial load/refresh - Calculate section from current URL
   * 2. Sidebar click navigation - Skip sync (already updated in onClick)
   * 3. External navigation (direct URL, browser back/forward) - Sync to correct section
   *
   * Key improvements:
   * - Uses lastSyncedPathRef instead of setTimeout to track sync state
   * - Handles initial null state properly
   * - Waits for permissions.visibleSections to be populated
   * - Does NOT include selectedSectionId in deps to prevent loops
   */
  useEffect(() => {
    // Wait for permissions to load - sections must exist before we can sync
    if (permissions.visibleSections.length === 0) {
      return;
    }

    // If sidebar navigation just happened, mark path as synced and skip
    if (isNavigatingFromSidebar.current) {
      lastSyncedPathRef.current = navigation.currentPath;
      isNavigatingFromSidebar.current = false;
      return;
    }

    // Skip if we already synced this path
    if (lastSyncedPathRef.current === navigation.currentPath) {
      return;
    }

    // Find the section that matches the current path
    const matchedSection = permissions.visibleSections.find(section => {
      return section.items?.some(item => {
        return isItemActive(navigation.currentPath, item.href, item.matchPaths);
      });
    });

    // Determine the target section
    const targetSection = matchedSection || permissions.visibleSections[0];

    if (targetSection) {
      setSelectedSectionId(prevId => {
        // If it's the same section, don't update
        if (prevId === targetSection.id) {
          return prevId;
        }

        // Only animate if it's NOT the initial load (prevId was null)
        if (prevId !== null) {
          setShouldAnimate(true);
        }

        return targetSection.id;
      });
    }

    // Update last synced path
    lastSyncedPathRef.current = navigation.currentPath;
  }, [navigation.currentPath, permissions.visibleSections]);

  // Encontrar la sección seleccionada
  const selectedSection = permissions.visibleSections.find(s => s.id === selectedSectionId) || permissions.visibleSections[0];

  // Animar cambios de sección con GSAP - SOLO cuando el usuario hace click en sidebar colapsado
  useEffect(() => {
    // Si no debemos animar, asegurar que items sean visibles
    if (!shouldAnimate) {
      setMenuItemsVisible(true);
      return;
    }

    // Cuando animamos, ocultar items primero, luego animar
    setMenuItemsVisible(false);

    // Pequeño delay para que React renderice con opacity 0
    const animationTimeout = setTimeout(() => {
      const ctx = gsap.context(() => {
        if (sectionTitleRef.current && menuItemsRef.current) {
          const menuItems = menuItemsRef.current.querySelectorAll('.menu-item');

          // Animar título
          gsap.fromTo(
            sectionTitleRef.current,
            { opacity: 0, x: -10 },
            {
              opacity: 1,
              x: 0,
              duration: 0.4,
              ease: 'power2.out',
              onComplete: () => setMenuItemsVisible(true),
            }
          );

          // Animar items con stagger
          gsap.fromTo(
            menuItems,
            { opacity: 0, x: -10 },
            {
              opacity: 1,
              x: 0,
              duration: 0.35,
              stagger: 0.03,
              ease: 'power2.out',
              delay: 0.1,
            }
          );
        }
      });

      return () => ctx.revert();
    }, 10);

    // Reset shouldAnimate después de iniciar la animación
    setShouldAnimate(false);

    return () => clearTimeout(animationTimeout);
  }, [selectedSectionId, shouldAnimate]);

  // ===========================
  // HANDLERS
  // ===========================

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutModal(false);
    logout();
    window.location.href = '/login';
  };

  const handleThemeSwitch = (checked: boolean) => {
    setTheme(checked ? 'dark' : 'light');
  };

  const handleOpenOrbitView = () => {
    if (!selectedProject) {
      alert('Por favor selecciona un proyecto primero');
      return;
    }

    if (projectOrbitViews.length === 0) {
      alert(`No hay vistas 360° disponibles para ${selectedProject.name}`);
      return;
    }

    const mainOrbitView = projectOrbitViews.find(v => v.orbit_type === 'project') || projectOrbitViews[0];
    const url = `${window.location.origin}/orbit-view/${mainOrbitView.slug}`;
    window.open(url, '_blank');
  };

  // ===========================
  // RENDER
  // ===========================

  // Si los permisos están cargando, mostrar skeleton
  if (permissions.isLoadingPermissions) {
    return (
      <div className={`flex h-full ${className}`}>
        <div className="w-20 h-full p-3 flex flex-col items-center">
          <div className="h-[72px] flex items-center justify-center">
            <div className="w-12 h-12 rounded-xl bg-gray-200 dark:bg-gray-700 animate-pulse" />
          </div>
          <div className="w-12 h-px bg-gray-300 dark:bg-gray-600 mb-4" />
          <div className="flex-1 flex flex-col gap-2 items-center">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="w-12 h-12 rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse" />
            ))}
          </div>
        </div>
        <div className="w-64 h-full p-3 pl-0">
          <div className="flex h-full flex-col bg-white/40 dark:bg-gray-800/40 backdrop-blur-xl rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  // Si no hay secciones visibles (permisos cargados pero sin acceso), mostrar sidebar mínimo
  if (!selectedSection) {
    return (
      <div className={`flex h-full ${className}`}>
        {/* Sidebar mínimo - Solo funciones esenciales */}
        <div className="w-20 h-full p-3 flex flex-col items-center">
          {/* Logo - Respeta la compañía del usuario */}
          <div className="h-[72px] flex items-center justify-center">
            {authUser?.company_data && !owlyMode ? (
              /* Client Company Logo */
              <CompanyIcon companyData={authUser.company_data} theme={theme} />
            ) : (
              /* OWLY Internal Logo */
              <div className="w-12 h-12 rounded-xl flex items-center justify-center">
                <OwlyLogo width={24} />
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="w-12 h-px bg-gray-300 dark:bg-gray-600 mb-4"></div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Footer - Funciones esenciales siempre visibles */}
          <div className="mt-auto pt-4 border-t border-gray-200/20 dark:border-gray-700/20 flex flex-col gap-2 items-center">
            {/* Theme Toggle */}
            <Tooltip content={theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'} position="right" delay={100}>
              <button
                onClick={toggleTheme}
                className="w-12 h-12 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/40 dark:hover:bg-white/20 hover:shadow-md hover:scale-105 transition-all duration-200 flex items-center justify-center"
              >
                {theme === 'dark' ? (
                  <MoonIcon className="h-5 w-5" />
                ) : (
                  <SunIcon className="h-5 w-5" />
                )}
              </button>
            </Tooltip>

            {/* Logout - SIEMPRE VISIBLE */}
            <Tooltip content="Cerrar Sesión" position="right" delay={100}>
              <button
                onClick={handleLogout}
                className="w-12 h-12 rounded-lg text-red-600 dark:text-red-400 hover:bg-red-100/20 dark:hover:bg-red-900/20 transition-all duration-200 flex items-center justify-center"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Panel derecho - Mensaje informativo */}
        <div className="w-64 h-full p-3 pl-0">
          <div className="flex h-full flex-col bg-white/40 dark:bg-gray-800/40 backdrop-blur-xl rounded-xl shadow-medium ring-1 ring-gray-950/10 dark:ring-white/10 overflow-hidden">
            {/* Header con user + company info */}
            <div className="h-[72px] flex items-center px-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                    {authUser?.full_name || authUser?.email || 'Usuario'}
                  </h1>
                  <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                    {authUser?.role && ROLE_CONFIG[authUser.role as keyof typeof ROLE_CONFIG]?.name || authUser?.role || 'Usuario'}
                  </span>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {authUser?.company_data?.name || 'OWLY'}
                </p>
              </div>
            </div>

            {/* Mensaje */}
            <div className="flex-1 flex items-center justify-center p-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <QuestionMarkCircleIcon className="h-8 w-8 text-amber-600 dark:text-amber-400" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                  Sin acceso configurado
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  No tienes módulos asignados. Contacta al administrador para obtener acceso.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-3">
              <a href="https://owlysales.ai" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 hover:opacity-80 transition-opacity">
                <OwlyLogo width={14} />
                <div className="flex flex-col">
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                    Powered by
                  </p>
                  <p className="text-xs font-bold text-primary-600 dark:text-primary-400 -mt-0.5">
                    owlysales.ai
                  </p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className={`flex h-full ${className}`}>
      {/* ============================================ */}
      {/* SIDEBAR IZQUIERDO - Solo Iconos (SIN FONDO) */}
      {/* ============================================ */}
      <div className="w-20 h-full p-3 flex flex-col items-center">
        {/* Logo de la compañía arriba - alineado con header del sidebar derecho */}
        <div className="h-[72px] flex items-center justify-center">
          {authUser?.company_data && !owlyMode ? (
            /* Client Company Logo */
            <CompanyIcon companyData={authUser.company_data} theme={theme} />
          ) : (
            /* OWLY Internal Logo */
            <div className="w-12 h-12 rounded-xl flex items-center justify-center">
              <OwlyLogo width={24} />
            </div>
          )}
        </div>

        {/* Divider — alineado con border-b del header derecho */}
        <div className="w-12 h-px bg-gray-300 dark:bg-gray-600"></div>

        {/* Iconos de navegación */}
        <nav className="flex-1 flex flex-col gap-2 overflow-y-auto items-center pt-4">
          {permissions.visibleSections.map((section) => {
            const SectionIcon = section.icon;
            const isSelected = selectedSectionId === section.id;
            // Use isItemActive for consistent path matching (supports wildcards and matchPaths)
            const hasActiveItem = section.items?.some(item => {
              return isItemActive(navigation.currentPath, item.href, item.matchPaths);
            });

            return (
              <Tooltip key={section.id} content={section.label} position="right" delay={100}>
                <button
                  onClick={() => {
                    // Marcar que la navegación viene del sidebar para evitar conflicto con useEffect
                    isNavigatingFromSidebar.current = true;
                    setShouldAnimate(true);
                    setSelectedSectionId(section.id);
                    // Navegar al primer item de la sección seleccionada
                    const filteredItems = permissions.filterItems(section.items || [], section.id, section.scope);
                    if (filteredItems.length > 0) {
                      navigation.handleNavigate(filteredItems[0].href);
                    }
                  }}
                  className={`w-12 h-12 rounded-lg transition-all duration-200 flex items-center justify-center ${
                    isSelected || hasActiveItem
                      ? 'bg-white/60 dark:bg-white/50 backdrop-blur-xl text-gray-900 dark:text-white shadow-lg scale-105'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/40 dark:hover:bg-white/20 hover:shadow-md hover:scale-105 opacity-60 hover:opacity-100'
                  }`}
                  aria-label={section.label}
                >
                  <span className="relative">
                    {SectionIcon && <SectionIcon className="h-5 w-5" />}
                    {section.id === 'communications' && unreadTeamCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-gray-900" />
                    )}
                  </span>
                </button>
              </Tooltip>
            );
          })}
        </nav>

        {/* Footer - Solo iconos esenciales */}
        <div className="mt-auto pt-4 border-t border-gray-200/20 dark:border-gray-700/20 flex flex-col gap-2 items-center">
          {/* OrbitView - controlled by gestion.maqueta-virtual permission */}
          {hasSubmodulePermission('gestion', 'maqueta-virtual') && (
            <Tooltip content="Maqueta Virtual - Visor 3D" position="right" delay={100}>
              <button
                onClick={handleOpenOrbitView}
                className="w-12 h-12 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 transition-all duration-200 shadow-md flex items-center justify-center"
              >
                <CubeTransparentIcon className="h-5 w-5 text-white" />
              </button>
            </Tooltip>
          )}

          {/* Theme Toggle */}
          <Tooltip content={theme === 'dark' ? 'Modo Claro' : 'Modo Oscuro'} position="right" delay={100}>
            <button
              onClick={toggleTheme}
              className="w-12 h-12 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/40 dark:hover:bg-white/20 hover:shadow-md hover:scale-105 opacity-60 hover:opacity-100 transition-all duration-200 flex items-center justify-center"
            >
              {theme === 'dark' ? (
                <MoonIcon className="h-5 w-5" />
              ) : (
                <SunIcon className="h-5 w-5" />
              )}
            </button>
          </Tooltip>

          {/* Notifications */}
          <Tooltip content="Notificaciones" position="right" delay={100}>
            <button
              className="w-12 h-12 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/40 dark:hover:bg-white/20 hover:shadow-md hover:scale-105 opacity-60 hover:opacity-100 transition-all duration-200 flex items-center justify-center relative"
            >
              <BellIcon className="h-5 w-5" />
              <span className="absolute top-2 right-2 h-2 w-2 bg-danger-500 rounded-full"></span>
            </button>
          </Tooltip>

          {/* Settings */}
          <Tooltip content="Configuración" position="right" delay={100}>
            <button
              className="w-12 h-12 rounded-lg text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/40 dark:hover:bg-white/20 hover:shadow-md hover:scale-105 opacity-60 hover:opacity-100 transition-all duration-200 flex items-center justify-center"
            >
              <CogIcon className="h-5 w-5" />
            </button>
          </Tooltip>

          {/* User Avatar — opens profile popover */}
          <Tooltip content={authUser?.full_name || 'Perfil'} position="right" delay={100}>
            <div
              ref={avatarRef}
              onClick={() => setShowProfilePopover(prev => !prev)}
              className="w-10 h-10 rounded-full cursor-pointer hover:scale-105 transition-all duration-200 relative mb-3"
            >
              {authUser && <UserAvatar user={authUser} size={40} />}
              <div className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-green-500 ring-2 ring-gray-100 dark:ring-gray-900" />
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Profile Popover */}
      {authUser && (
        <UserProfilePopover
          user={authUser}
          isOpen={showProfilePopover}
          onClose={() => setShowProfilePopover(false)}
          onLogout={handleLogout}
          onEditProfile={() => setShowEditProfileModal(true)}
          anchorRef={avatarRef}
        />
      )}

      {/* Edit Profile Modal */}
      {authUser && (
        <EditProfileModal
          user={authUser}
          isOpen={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
          onSaved={refreshUser}
        />
      )}

      {/* ============================================ */}
      {/* SIDEBAR DERECHO - Submenús (CON GLASSMORPHISM) */}
      {/* ============================================ */}
      <div className="w-64 h-full p-3 pl-0">
        <nav className="flex h-full flex-col bg-white/40 dark:bg-gray-800/40 backdrop-blur-xl rounded-xl shadow-medium ring-1 ring-gray-950/10 dark:ring-white/10 transition-colors duration-300 overflow-hidden relative">
          {/* Header con user + company info — altura alineada con logo del sidebar izquierdo */}
          <div className="h-[72px] flex items-center px-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-bold text-gray-900 dark:text-white truncate">
                  {authUser?.full_name || authUser?.email || 'Usuario'}
                </h1>
                <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                  {authUser?.role && ROLE_CONFIG[authUser.role as keyof typeof ROLE_CONFIG]?.name || authUser?.role || 'Usuario'}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {authUser?.company_data?.name || 'OWLY'}
              </p>
            </div>
          </div>

          {/* Header de la sección seleccionada */}
          <div
            ref={sectionTitleRef}
            className="px-7 pt-4 pb-2"
            style={{ opacity: menuItemsVisible ? 1 : 0 }}
          >
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">
                {selectedSection.label}
              </h2>
              {/* PLATFORM tag for platform-only sections */}
              {selectedSection.platformTag === 'PLATFORM' && (
                <span className="px-1.5 py-0.5 text-[9px] font-bold bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300 rounded uppercase tracking-wider">
                  OWLY
                </span>
              )}
            </div>
          </div>

          {/* Submenús de la sección seleccionada */}
          <div className="flex-1 flex flex-col overflow-y-auto px-4 pb-4 gap-1">
            <div
              ref={menuItemsRef}
              className="flex flex-col gap-1"
            >
              {selectedSection.items && permissions.filterItems(selectedSection.items, selectedSection.id, selectedSection.scope).map((item, index) => {
                // Use enhanced isItemActive that supports matchPaths and pipeline type detection
                const isActive = isItemActiveEnhanced(navigation.currentPath, item);
                const ItemIcon = item.icon;

                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      isNavigatingFromSidebar.current = true;
                      navigation.handleNavigate(item.href);
                    }}
                    style={{ opacity: menuItemsVisible ? 1 : 0, transition: 'opacity 0.15s ease' }}
                    className={`menu-item flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 text-left ${
                      isActive
                        ? 'bg-white/90 dark:bg-white/90 backdrop-blur-xl text-gray-900 dark:text-gray-900 font-medium shadow-lg'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {ItemIcon && <ItemIcon className="h-4 w-4 flex-shrink-0" />}
                    <span className="flex-1">{item.label}</span>
                    {/* PLATFORM tag - shows for platform-only items */}
                    {item.platformTag === 'PLATFORM' && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold bg-violet-100 dark:bg-violet-900/50 text-violet-700 dark:text-violet-300 rounded uppercase tracking-wider">
                        OWLY
                      </span>
                    )}
                    {/* Regular badge */}
                    {item.badge && !item.platformTag && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-primary-100 dark:bg-primary-900/50 text-primary-700 dark:text-primary-300 rounded-full">
                        {item.badge}
                      </span>
                    )}
                    {/* Unread badge for team-chat */}
                    {item.id === 'team-chat' && unreadTeamCount > 0 && (
                      <span className="min-w-[20px] px-1.5 py-0.5 text-[10px] font-bold bg-red-500 text-white rounded-full text-center">
                        {unreadTeamCount > 99 ? '99+' : unreadTeamCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Promo Card - Justo arriba del footer */}
          <SidebarPromoCard
            variant="upgrade"
            onCtaClick={() => {
              window.open('https://owlysales.ai/pricing', '_blank');
            }}
          />

          {/* Footer - Powered by OWLY */}
          <div className="border-t border-gray-200 dark:border-gray-800 px-4 py-3">
            <a href="https://owlysales.ai" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 hover:opacity-80 transition-opacity">
              <OwlyLogo width={14} />
              <div className="flex flex-col">
                <p className="text-xs font-semibold text-gray-600 dark:text-gray-400">
                  Powered by
                </p>
                <p className="text-xs font-bold text-primary-600 dark:text-primary-400 -mt-0.5">
                  owlysales.ai
                </p>
              </div>
            </a>
          </div>
        </nav>
      </div>
    </div>

    {/* Modal de confirmación de cierre de sesión */}
    {showLogoutModal && createPortal(
      <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-6 text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <ArrowRightOnRectangleIcon className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Cerrar Sesión
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              ¿Estás seguro de que deseas cerrar sesión?
            </p>
          </div>
          <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex justify-end gap-3">
            <button
              onClick={() => setShowLogoutModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleLogoutConfirm}
              className="px-4 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
            >
              Cerrar Sesión
            </button>
          </div>
        </div>
      </div>,
      document.body
    )}
    </>
  );
};
